import { Injectable, isDevMode, inject, InjectionToken } from '@angular/core';
import { LogLevel, LogEntry, LogConfig } from './logger.models';
import { DEFAULT_LOG_CONFIG, LOCALSTORAGE_KEY } from './logger.config';

export interface EnvironmentConfig {
  production: boolean;
}

export const ENVIRONMENT_TOKEN = new InjectionToken<EnvironmentConfig>(
  'environment',
);

@Injectable({
  providedIn: 'root',
})
export class FrontendLoggerService {
  private logs: LogEntry[] = [];
  private pendingBackendLogs: LogEntry[] = [];
  private config: LogConfig = { ...DEFAULT_LOG_CONFIG };
  private batchTimer?: ReturnType<typeof setTimeout>;
  private readonly environment = inject(ENVIRONMENT_TOKEN, { optional: true });

  constructor() {
    this.config.enableConsole = this.shouldEnableConsole();
  }

  private shouldEnableConsole(): boolean {
    if (this.environment) {
      return !this.environment.production;
    }
    return isDevMode();
  }

  error(message: string, error?: unknown, context?: string): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, error, context);

    if (this.config.enableConsole) {
      console.error(`[${context || 'Frontend'}]`, message, error);
    }

    this.addLog(entry);

    if (this.config.enableLocalStorage) {
      this.persistToLocalStorage(entry);
    }

    if (this.config.enableBackendTransport) {
      this.queueForBackend(entry);
    }
  }

  critical(message: string, error?: unknown, context?: string): void {
    const entry = this.createLogEntry(
      LogLevel.CRITICAL,
      message,
      error,
      context,
    );

    if (this.config.enableConsole) {
      console.error(`[CRITICAL] [${context || 'Frontend'}]`, message, error);
    }

    this.addLog(entry);

    if (this.config.enableLocalStorage) {
      this.persistToLocalStorage(entry);
    }

    if (this.config.enableBackendTransport) {
      this.queueForBackend(entry);
    }
  }

  warn(message: string, data?: unknown, context?: string): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, data, context);

    if (this.config.enableConsole) {
      console.warn(`[${context || 'Frontend'}]`, message, data);
    }

    this.addLog(entry);

    if (this.config.enableBackendTransport) {
      this.queueForBackend(entry);
    }
  }

  info(message: string, data?: unknown, context?: string): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, data, context);

    if (this.config.enableConsole) {
      console.info(`[${context || 'Frontend'}]`, message, data);
    }

    this.addLog(entry);

    if (this.config.enableBackendTransport) {
      this.queueForBackend(entry);
    }
  }

  debug(message: string, data?: unknown, context?: string): void {
    if (!this.config.enableConsole) {
      return;
    }

    const entry = this.createLogEntry(LogLevel.DEBUG, message, data, context);

    console.debug(`[${context || 'Frontend'}]`, message, data);

    this.addLog(entry);
  }

  updateConfig(newConfig: Partial<LogConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  getLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  clearLocalStorage(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(LOCALSTORAGE_KEY);
    }
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: unknown,
    context?: string,
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      context,
    };

    if (data) {
      entry.data = this.sanitizeData(data);

      if (data instanceof Error) {
        entry.stackTrace = data.stack;
      }
    }

    if (typeof window !== 'undefined') {
      entry.userAgent = navigator.userAgent;
      entry.url = window.location.href;
    }

    return entry;
  }

  private addLog(entry: LogEntry): void {
    this.logs.push(entry);

    const maxLogs = this.config.maxLogs || 1000;
    if (this.logs.length > maxLogs) {
      this.logs.shift();
    }
  }

  private persistToLocalStorage(entry: LogEntry): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      const errors: LogEntry[] = stored ? JSON.parse(stored) : [];

      errors.push(entry);

      if (errors.length > 50) {
        errors.shift();
      }

      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(errors));
    } catch (err) {
      console.error('Failed to persist log to localStorage:', err);
    }
  }

  private queueForBackend(entry: LogEntry): void {
    this.pendingBackendLogs.push(entry);

    if (this.pendingBackendLogs.length >= this.config.batchSize) {
      this.flushToBackend();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.flushToBackend();
      }, this.config.batchInterval);
    }
  }

  private flushToBackend(): void {
    if (this.pendingBackendLogs.length === 0) {
      return;
    }

    const logsToSend = [...this.pendingBackendLogs];
    this.pendingBackendLogs = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }

    console.log(
      `[FrontendLogger] Would send ${logsToSend.length} logs to backend (Phase 4)`,
    );
  }

  private sanitizeData(data: unknown): unknown {
    if (!data || typeof data !== 'object') {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    const sanitized: Record<string, unknown> = {};
    const sensitiveKeys = this.config.sensitiveKeys || [];
    const dataRecord = data as Record<string, unknown>;

    for (const key in dataRecord) {
      if (Object.prototype.hasOwnProperty.call(dataRecord, key)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveKeys.some(sensitiveKey =>
          lowerKey.includes(sensitiveKey.toLowerCase()),
        );

        if (isSensitive) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof dataRecord[key] === 'object') {
          sanitized[key] = this.sanitizeData(dataRecord[key]);
        } else {
          sanitized[key] = dataRecord[key];
        }
      }
    }

    return sanitized;
  }
}
